import { useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';

import { ApiError } from '../../shared/api/client';
import { calendarDay } from '../../shared/lib/format';
import { Button } from '../../shared/ui/button';
import { EventsIcon } from '../../shared/ui/icons';
import { Page, PageHeader } from '../../shared/ui/layout';
import { ErrorState, LoadingState } from '../../shared/ui/states';
import {
  ClientStatusBanner,
  clientSourceOf,
  clientStatus,
  sourceClientKeys,
  sourceCustomer,
  type TSourceCustomer,
} from '../../entities/customer';
import { EMPTY_RUN_FILTERS, runFiltersToSearch } from '../../entities/run';
import { getSource, type TSource } from '../../entities/source';
import { ClientDetails } from '../../features/client-details';
import { ClientRemove } from '../../features/client-remove';
import { ClientSecretCard } from '../../features/source-secrets';
import { ClientAddressCard } from './ui/client-address-card';
import { ClientFlowsCard } from './ui/client-flows-card';
import { ClientGone } from './ui/client-gone';
import { ClientTrafficCard } from './ui/client-traffic-card';

interface ISourceClientPageProps {
  sourceId: string;
  customerId: string;
  /** The list's search this page was opened from, so going back to it keeps the filter. */
  listQ?: string;
  timeZone?: string;
}

const Crumbs = ({ source, listQ }: { source: TSource; listQ?: string }) => (
  <span className="flex min-w-0 items-center gap-2">
    <Link to="/sources">Sources</Link>
    <span className="text-faint">/</span>
    <Link to="/sources/$sourceId" params={{ sourceId: source.id }} className="truncate">
      {source.name}
    </Link>
    <span className="text-faint">/</span>
    <Link to="/sources/$sourceId/clients" params={{ sourceId: source.id }} search={{ q: listQ }}>
      Clients
    </Link>
  </span>
);

const RefTag = ({ children }: { children: ReactNode }) => (
  <span className="max-w-ref-tag truncate rounded-tag bg-chip-on px-label py-px font-mono text-micro text-primary">
    {children}
  </span>
);

// Their runs over the window the counts cover, on the Flows view of Events.
const eventsSearch = (row: TSourceCustomer, sourceId: string) =>
  runFiltersToSearch({
    ...EMPTY_RUN_FILTERS,
    minutes: row.customer.activity.windowHours * 60,
    customerId: [row.customer.id],
    sourceId: [sourceId],
  });

const ClientScreen = ({
  source,
  row,
  listQ,
  timeZone,
}: {
  source: TSource;
  row: TSourceCustomer;
  listQ?: string;
  timeZone?: string;
}) => {
  const navigate = useNavigate();
  const now = useMemo(() => Date.now(), [row]);
  const { customer } = row;
  const facts = clientSourceOf(source);
  const toList = () =>
    void navigate({
      to: '/sources/$sourceId/clients',
      params: { sourceId: source.id },
      search: { q: listQ },
    });

  if (row.state === 'revoked') {
    return (
      <Page width="full">
        <div className="text-meta text-muted">
          <Crumbs source={source} listQ={listQ} />
        </div>
        <ClientGone
          title={`${customer.name} was removed`}
          actions={
            <>
              <Button
                size="sm"
                variant="secondary"
                onClick={() =>
                  void navigate({ to: '/events', search: eventsSearch(row, source.id) })
                }
              >
                See their runs
              </Button>
              <Button size="sm" variant="ghost" onClick={toList}>
                All clients
              </Button>
            </>
          }
        >
          Their address stopped working when they were removed. Their runs stay in Events, marked
          with the removed client.
        </ClientGone>
      </Page>
    );
  }

  return (
    <>
      <PageHeader
        width="full"
        title={customer.name}
        badge={customer.externalId ? <RefTag>{customer.externalId}</RefTag> : undefined}
        breadcrumbs={<Crumbs source={source} listQ={listQ} />}
        description={`A client of ${source.name} since ${calendarDay(row.attachedAt, now, timeZone)}`}
        actions={
          <Button
            size="sm"
            variant="secondary"
            onClick={() => void navigate({ to: '/events', search: eventsSearch(row, source.id) })}
          >
            <EventsIcon size={12} /> See their events
          </Button>
        }
      />
      <Page width="full">
        <ClientStatusBanner status={clientStatus(row, facts, now, timeZone)} />
        <div className="flex items-start gap-5">
          <div className="flex min-w-0 flex-1 flex-col gap-5">
            <ClientAddressCard source={source} url={row.ingestUrl} />
            <ClientSecretCard
              sourceId={source.id}
              source={facts}
              row={row}
              now={now}
              timeZone={timeZone}
            />
            <ClientTrafficCard row={row} withSecret={facts.secrets} now={now} timeZone={timeZone} />
          </div>
          <div className="flex w-panel-md shrink-0 flex-col gap-5">
            <ClientDetails key={customer.id} customer={customer} now={now} timeZone={timeZone} />
            <ClientFlowsCard
              sourceId={source.id}
              sourceName={source.name}
              clientName={customer.name}
            />
            <ClientRemove sourceId={source.id} row={row} onRemoved={toList} />
          </div>
        </div>
      </Page>
    </>
  );
};

const isNotFound = (error: unknown): boolean => error instanceof ApiError && error.status === 404;

export const SourceClientPage = ({
  sourceId,
  customerId,
  listQ,
  timeZone,
}: ISourceClientPageProps) => {
  const source = useQuery({ queryKey: ['source', sourceId], queryFn: () => getSource(sourceId) });
  const row = useQuery({
    queryKey: sourceClientKeys.one(sourceId, customerId),
    queryFn: () => sourceCustomer(sourceId, customerId),
  });

  if (isNotFound(source.error)) {
    return (
      <Page width="full">
        <div className="text-meta text-muted">
          <Link to="/sources">Sources</Link>
        </div>
        <ClientGone
          title="This source doesn’t exist"
          actions={
            <Link to="/sources" className="text-data text-primary underline">
              All sources
            </Link>
          }
        >
          There is no source with this id in your organization, so no client of it either. It may
          have been deleted, or the link is mistyped.
        </ClientGone>
      </Page>
    );
  }

  const failed = source.error ?? row.error;
  if (failed) {
    if (source.data && isNotFound(failed)) {
      return (
        <Page width="full">
          <div className="text-meta text-muted">
            <Crumbs source={source.data} listQ={listQ} />
          </div>
          <ClientGone
            title={`Not a client of ${source.data.name}`}
            actions={
              <Link
                to="/sources/$sourceId/clients"
                params={{ sourceId }}
                search={{ q: listQ }}
                className="text-data text-primary underline"
              >
                All clients
              </Link>
            }
          >
            Nobody with this id has an address on {source.data.name}. The link may be from another
            source, or mistyped.
          </ClientGone>
        </Page>
      );
    }
    return (
      <div className="p-page">
        <ErrorState
          error={failed}
          onRetry={() => {
            void source.refetch();
            void row.refetch();
          }}
        />
      </div>
    );
  }

  if (!source.data || !row.data) {
    return (
      <div className="p-page">
        <LoadingState rows={8} />
      </div>
    );
  }

  return <ClientScreen source={source.data} row={row.data} listQ={listQ} timeZone={timeZone} />;
};
